/**
 * Formation Status Value Object
 *
 * Defines all valid formation statuses with validation.
 * Expanded from 8 to 12 statuses to match business documentation.
 *
 * Status Flow:
 * - Delaware: draft → verified → in_progress → state_review → formation_done →
 *             ein_ss4_sent → ein_ss4_signed → ein_fax_sent → ein_fax_delivered →
 *             completed
 *
 * - Wyoming:  draft → verified → formation_done →
 *             ein_ss4_sent → ein_ss4_signed → ein_fax_sent → ein_fax_delivered →
 *             completed
 */

export const FormationStatus = {
  // Pre-filing statuses
  PENDING: 'pending', // Legacy: existing orders may have this; treated same as draft
  DRAFT: 'draft',
  VERIFIED: 'verified',

  // Delaware-only signing statuses
  IN_PROGRESS: 'in_progress', // DocuSeal signature pending
  STATE_REVIEW: 'state_review', // Signed, awaiting state approval

  // Post-filing status
  FORMATION_DONE: 'formation_done', // State certificate received

  // EIN sub-workflow statuses (granular tracking)
  EIN_SS4_SENT: 'ein_ss4_sent', // SS-4 DocuSeal sent to customer
  EIN_SS4_SIGNED: 'ein_ss4_signed', // Customer signed SS-4
  EIN_FAX_SENT: 'ein_fax_sent', // Fax sent to IRS
  EIN_FAX_DELIVERED: 'ein_fax_delivered', // Telnyx confirmed delivery
  /** @deprecated Legacy alias for `ein_fax_delivered`. Kept for backward compatibility with existing DB records. */
  EIN_AWAITING_LETTER: 'ein_awaiting_letter', // Legacy: same as ein_fax_delivered

  // Legacy EIN status (for backward compatibility)
  EIN_PROGRESS: 'ein_progress', // Maps to ein_ss4_sent in new system

  // Terminal statuses
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
} as const

export type FormationStatusType = (typeof FormationStatus)[keyof typeof FormationStatus]

/**
 * All valid formation status values
 */
export const ALL_FORMATION_STATUSES: FormationStatusType[] = Object.values(FormationStatus)

/**
 * EIN sub-workflow statuses
 */
export const EIN_SUBSTATUS_VALUES: FormationStatusType[] = [
  FormationStatus.EIN_SS4_SENT,
  FormationStatus.EIN_SS4_SIGNED,
  FormationStatus.EIN_FAX_SENT,
  FormationStatus.EIN_FAX_DELIVERED,
  FormationStatus.EIN_AWAITING_LETTER,
]

/**
 * Terminal statuses (workflow complete)
 */
export const TERMINAL_STATUSES: FormationStatusType[] = [FormationStatus.COMPLETED, FormationStatus.CANCELLED]

/**
 * Delaware-only statuses (Wyoming skips these)
 */
export const DELAWARE_ONLY_STATUSES: FormationStatusType[] = [FormationStatus.IN_PROGRESS, FormationStatus.STATE_REVIEW]

/**
 * Status display labels
 */
const FORMATION_STATUS_LABELS: Record<FormationStatusType, string> = {
  [FormationStatus.PENDING]: 'Getting Started', // Legacy alias
  [FormationStatus.DRAFT]: 'Getting Started',
  [FormationStatus.VERIFIED]: 'Information Verified',
  [FormationStatus.IN_PROGRESS]: 'Document Signing',
  [FormationStatus.STATE_REVIEW]: 'State Review',
  [FormationStatus.FORMATION_DONE]: 'Formation Complete',
  [FormationStatus.EIN_SS4_SENT]: 'SS-4 Sent',
  [FormationStatus.EIN_SS4_SIGNED]: 'SS-4 Signed',
  [FormationStatus.EIN_FAX_SENT]: 'Fax to IRS Sent',
  [FormationStatus.EIN_FAX_DELIVERED]: 'Fax Delivered',
  [FormationStatus.EIN_AWAITING_LETTER]: 'Fax Delivered', // Legacy alias
  [FormationStatus.EIN_PROGRESS]: 'Getting EIN', // Legacy
  [FormationStatus.COMPLETED]: 'All Done!',
  [FormationStatus.CANCELLED]: 'Cancelled',
}

/**
 * Status progress percentages (for UI)
 */
const DELAWARE_PROGRESS: Record<FormationStatusType, number> = {
  [FormationStatus.PENDING]: 0, // Legacy alias
  [FormationStatus.DRAFT]: 0,
  [FormationStatus.VERIFIED]: 10,
  [FormationStatus.IN_PROGRESS]: 20,
  [FormationStatus.STATE_REVIEW]: 30,
  [FormationStatus.FORMATION_DONE]: 45,
  [FormationStatus.EIN_SS4_SENT]: 55,
  [FormationStatus.EIN_SS4_SIGNED]: 65,
  [FormationStatus.EIN_FAX_SENT]: 75,
  [FormationStatus.EIN_FAX_DELIVERED]: 85,
  [FormationStatus.EIN_AWAITING_LETTER]: 85, // Legacy alias
  [FormationStatus.EIN_PROGRESS]: 55, // Legacy
  [FormationStatus.COMPLETED]: 100,
  [FormationStatus.CANCELLED]: 0,
}

const WYOMING_PROGRESS: Record<FormationStatusType, number> = {
  [FormationStatus.PENDING]: 0, // Legacy alias
  [FormationStatus.DRAFT]: 0,
  [FormationStatus.VERIFIED]: 15,
  [FormationStatus.IN_PROGRESS]: 15, // Should not occur
  [FormationStatus.STATE_REVIEW]: 15, // Should not occur
  [FormationStatus.FORMATION_DONE]: 35,
  [FormationStatus.EIN_SS4_SENT]: 50,
  [FormationStatus.EIN_SS4_SIGNED]: 60,
  [FormationStatus.EIN_FAX_SENT]: 75,
  [FormationStatus.EIN_FAX_DELIVERED]: 90,
  [FormationStatus.EIN_AWAITING_LETTER]: 90, // Legacy alias
  [FormationStatus.EIN_PROGRESS]: 50, // Legacy
  [FormationStatus.COMPLETED]: 100,
  [FormationStatus.CANCELLED]: 0,
}

/**
 * Check if a value is a valid formation status
 */
export function isValidFormationStatus(value: unknown): value is FormationStatusType {
  return typeof value === 'string' && (ALL_FORMATION_STATUSES as readonly string[]).includes(value)
}

/**
 * Check if status is terminal (completed or cancelled)
 */
export function isTerminalStatus(status: FormationStatusType): boolean {
  return TERMINAL_STATUSES.includes(status)
}

/**
 * Check if status is in EIN sub-workflow
 */
export function isEinSubstatus(status: FormationStatusType): boolean {
  return EIN_SUBSTATUS_VALUES.includes(status) || status === FormationStatus.EIN_PROGRESS
}

/**
 * Check if status is Delaware-only
 */
export function isDelawareOnlyStatus(status: FormationStatusType): boolean {
  return DELAWARE_ONLY_STATUSES.includes(status)
}

/**
 * Get progress percentage for a status
 */
export function getStatusProgress(status: FormationStatusType, state: 'DE' | 'WY'): number {
  const progressMap = state === 'DE' ? DELAWARE_PROGRESS : WYOMING_PROGRESS
  return progressMap[status] ?? 0
}

/**
 * Get display label for a status
 */
export function getStatusLabel(status: FormationStatusType): string {
  return FORMATION_STATUS_LABELS[status] ?? status
}

/**
 * Map legacy statuses to their canonical equivalents.
 * - ein_progress → ein_ss4_sent
 * - ein_awaiting_letter → ein_fax_delivered (redundant alias)
 * @throws Error if status is invalid
 */
export function mapLegacyEinStatus(legacyStatus: string): FormationStatusType {
  if (legacyStatus === FormationStatus.EIN_PROGRESS) {
    return FormationStatus.EIN_SS4_SENT
  }
  if (legacyStatus === FormationStatus.EIN_AWAITING_LETTER) {
    return FormationStatus.EIN_FAX_DELIVERED
  }
  if (!isValidFormationStatus(legacyStatus)) {
    throw new Error(`Invalid formation status: ${legacyStatus}`)
  }
  return legacyStatus
}
